import { type ReactNode } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';

import type { AxisTriplet } from '../../../lib/skeleton/boneTransforms';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import AxisTripletFields from './AxisTripletFields';

export default function TransformAxisValuesRow({
  label,
  labelIcon,
  compact,
  values,
  editable = false,
  ariaLabelPrefix,
  onAxisChange,
  baselineValues,
  changeEpsilon,
  dragDeltaPerPixel,
  onValueChangeStart,
  onValueChangeEnd,
  onDragValueChangeStart,
}: {
  label: string;
  labelIcon: ReactNode;
  compact: boolean;
  values: AxisTriplet;
  editable?: boolean;
  /** Defaults to `label` for axis field aria labels. */
  ariaLabelPrefix?: string;
  onAxisChange?: (axisIndex: number, nextValue: number) => void;
  baselineValues?: AxisTriplet | null;
  changeEpsilon?: number;
  dragDeltaPerPixel?: number;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
  onDragValueChangeStart?: () => void;
}) {
  const prefix = ariaLabelPrefix ?? label;

  return (
    <div className="flex items-center gap-2 py-1">
      <div className={`${compact ? 'w-6' : 'w-16'} shrink-0`}>
        {compact ? (
          <Tooltip text={label} side="top" inModal>
            <span className="border-gray-30/80 dark:border-gray-60/80 bg-white/45 dark:bg-gray-90/45 text-primary inline-flex h-5 w-5 items-center justify-center rounded-md border">
              {labelIcon}
            </span>
          </Tooltip>
        ) : (
          <Text
            variant="studio/body/medium/xs"
            className={`${PANEL_WRITING_CLASSNAME} block truncate`}
          >
            {label}
          </Text>
        )}
      </div>
      <AxisTripletFields
        values={values}
        editable={editable}
        ariaLabelPrefix={prefix}
        onAxisChange={onAxisChange}
        baselineValues={baselineValues}
        changeEpsilon={changeEpsilon}
        dragDeltaPerPixel={dragDeltaPerPixel}
        onValueChangeStart={onValueChangeStart}
        onValueChangeEnd={onValueChangeEnd}
        onDragValueChangeStart={onDragValueChangeStart}
      />
      <span
        aria-label="Local space"
        className="border-gray-30/60 dark:border-gray-60/60 bg-white/20 dark:bg-gray-90/20 inline-flex h-5 min-w-5 shrink-0 cursor-default select-none items-center justify-center rounded-md border px-1 tabular-nums"
      >
        <Text as="span" variant="studio/body/medium/xs" className="text-secondary">
          L
        </Text>
      </span>
    </div>
  );
}
